import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { SESSION_CONFIG } from '../config/session';

interface SessionManagerOptions {
  logoutOnWindowClose?: boolean; // Clear session when browser/tab closes
  validateOnMount?: boolean; // Check session validity when component mounts
}

/**
 * Hook to manage session lifecycle
 * - Validates session on mount
 * - Optionally clears session when window/tab closes
 * - Redirects to login if session is invalid
 */
export const useSessionManager = (options: SessionManagerOptions = {}) => {
  const { 
    logoutOnWindowClose = SESSION_CONFIG.LOGOUT_ON_WINDOW_CLOSE, 
    validateOnMount = true 
  } = options;

  const navigate = useNavigate();
  const checkSession = useAuthStore((state) => state.checkSession);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasCheckedSession = useRef(false);

  // Validate session on mount
  useEffect(() => {
    if (validateOnMount && isAuthenticated && !hasCheckedSession.current) {
      hasCheckedSession.current = true;
      const isValid = checkSession();
      
      if (!isValid) {
        console.log('[Session] Invalid session detected, redirecting to login');
        clearAuth();
        navigate('/auth/login', { replace: true });
      }
    }
  }, [validateOnMount, isAuthenticated, checkSession, clearAuth, navigate]);

  // Handle window/tab close
  useEffect(() => {
    if (!logoutOnWindowClose || !isAuthenticated) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Clear authentication when window is closing
      clearAuth();
      
      // Note: Modern browsers ignore custom messages, but we still need to return something
      // to trigger the handler
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [logoutOnWindowClose, isAuthenticated, clearAuth]);

  // Periodic session validation (every minute)
  useEffect(() => {
    if (!isAuthenticated) return;

    const intervalId = setInterval(() => {
      const isValid = checkSession();
      
      if (!isValid) {
        console.log('[Session] Session validation failed');
        clearAuth();
        navigate('/auth/login', { replace: true });
      }
    }, SESSION_CONFIG.SESSION_CHECK_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated, checkSession, clearAuth, navigate]);
};
